import React, { useState, useEffect, Fragment } from "react"

import ServicesCart from "./components/ServicesCart"
import AvailableDeliverySlots from "./components/AvailableDeliverySlots"

import BrowserLocationHelper from "../utils/browser_location_helper"
import { getFromLocalStorage } from "../utils/localStorage"
import { intercomTrackEvent } from "../utils/Intercom/intercomHelper"
import { registerBeginCheckoutToFacebook } from "../apis/Analytics/registerOrderToFacebook"

import { GetAvailableDeliverySlots } from "../apis/VenuesAPI"
import { logBeginCheckout } from "../apis/Analytics/gtmActions"
import { ORDER_MAX_CASH_THRESHOLD } from "../utils/config"
import {
  ADD_TO_CART,
  getCart,
  REMOVE_FROM_CART,
  UPDATE_DELIVERY_DETAILS,
  useCartStateStore,
} from "../stores/CartStateStore"
import DiscountedCartProgressBar from "./components/DiscountedCartProgressBar"
import CheckoutButton from "./components/CheckoutButton/index.jsx"

import style from "./CartContainer.module.scss"

function getWrongAddressError() {
  return (
    <div>
      <div className="alert alert-danger">
        <b className="change-delivery-address-link">
          {I18n.t("service_ordering.cannot_order_with_address")}
        </b>
      </div>
    </div>
  )
}

function getMissingAddressError() {
  return (
    <div>
      <div>
        <b className="change-delivery-address-link">
          {I18n.t("service_ordering.delivery_address_missing")}
        </b>
      </div>
    </div>
  )
}

function getRestaurantNotSupported() {
  return (
    <div>
      <div className="alert alert-warning">
        {I18n.t("service_ordering.we_are_sorry_change_restaurant")}
      </div>
    </div>
  )
}

function minDeliveryPrice(slots) {
  if (slots && slots.length > 0) {
    return Math.min(...slots.map((slot) => slot.price_cents))
  } else {
    return null
  }
}

function maxDeliveryPrice(slots) {
  if (slots && slots.length > 0) {
    return Math.max(...slots.map((slot) => slot.price_cents))
  } else {
    return null
  }
}

export default function CartContainer(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const {
    containerStyle,
    innerStyle,
    storeId,
    latitude,
    longitude,
    minGoodsPrice = 0,
    orderingEnabled,
    isModal,
    padded = false,
  } = props

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [state, setState] = useState({
    deliverySlots: null,
    checkoutInProgress: false,
    isTakeawayMode: JSON.parse(getFromLocalStorage("take_away")) === true,
    wait: true,
  })

  const [cartState, dispatchCartState] = useCartStateStore()

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    _fetchDeliverySlots(storeId, latitude, longitude, state.isTakeawayMode)
  }, [])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  async function _fetchDeliverySlots(
    storeId,
    latitude,
    longitude,
    isTakeawayActive
  ) {
    try {
      const slots = await GetAvailableDeliverySlots(
        storeId,
        latitude,
        longitude,
        isTakeawayActive
      )
      // TODO: completely remove deliverySlots (no setState here apart for wait: false)
      let min = minDeliveryPrice(slots)
      let max = maxDeliveryPrice(slots)
      setState((state) => ({
        ...state,
        deliverySlots: slots,
        minDeliveryPrice: min,
        maxDeliveryPrice: max,
        wait: false,
      }))
      dispatchCartState({
        type: UPDATE_DELIVERY_DETAILS,
        payload: { price: min, errors: [] },
      })
    } catch (errors) {
      setState((state) => ({ ...state, wait: false }))
      dispatchCartState({
        type: UPDATE_DELIVERY_DETAILS,
        payload: { price: 0, errors: errors },
      })
    }
  }

  function isDeliveryAddressFilled() {
    const errors = cartState.deliveryDetails.errors
    return !(errors && errors.indexOf("invalid-address") !== -1)
  }

  function isDeliveryAddressSupported() {
    const errors = cartState.deliveryDetails.errors
    return !(errors && errors.indexOf("long-distance") !== -1)
  }

  function isGoodsTotalPriceEnough() {
    const { subtotal } = getCart(cartState)

    // GUARD
    if (minGoodsPrice === 0) {
      return true
    }

    return subtotal >= minGoodsPrice
  }

  function isCheckoutButtonEnabled() {
    return (
      (state.isTakeawayMode || cartState.deliveryDetails?.price !== null) &&
      Object.keys(getCart(cartState).products || {}).length > 0 &&
      isGoodsTotalPriceEnough()
    )
  }

  function handleIncreaseQuantityClicked(sku, service) {
    dispatchCartState({
      type: ADD_TO_CART,
      payload: { sku: sku, update: service },
    })
  }

  function handleDecreaseQuantityClicked(sku) {
    dispatchCartState({ type: REMOVE_FROM_CART, payload: { sku: sku } })
  }

  function handleCheckoutButtonClicked() {
    if (state.checkoutInProgress) {
      return // disabling button is not enough. Added a guard.
    }

    setState({ ...state, checkoutInProgress: true })

    const path = `/${I18n.locale}/orders/new?venue_id=${cartState.store_id}`

    let cart = getCart(cartState)
    let orderLineItems = []
    for (var product in cart.products) {
      orderLineItems.push(cart.products[product].name)
    }
    // Intercom event CartCreated
    intercomTrackEvent("cart-created", {
      "Created at": new Date(),
      "Order line items": orderLineItems.join(", "),
      "Venue slug": window.location.pathname.substr(
        window.location.pathname.lastIndexOf("/") + 1
      ),
    })

    logBeginCheckout(cart.products, (cart.total || 0) / 100.0)
    registerBeginCheckoutToFacebook((cart.total || 0) / 100.0)

    BrowserLocationHelper.ChangeURL(path)
  }

  // -------------------------------------
  // GUARDS
  // -------------------------------------
  if (!orderingEnabled) {
    return getRestaurantNotSupported()
  }
  if (state.wait) {
    return (
      <div>
        <p className="text-center">{I18n.t("wait")}</p>
      </div>
    )
  }
  if (!isDeliveryAddressFilled()) {
    return getMissingAddressError()
  }
  if (!isDeliveryAddressSupported()) {
    return getWrongAddressError()
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  const cart = getCart(cartState)
  const cartPromotion = cart.promotion
  const minPrice = state.minDeliveryPrice
  const maxPrice = state.maxDeliveryPrice
  const multipleDeliveryPrices = minPrice !== maxPrice

  return (
    <div
      className={`${style.cartContainer} ${
        padded ? style.containerPadding : ""
      }`}
    >
      <div
        className={`${style.cartInnerContainer} ${
          padded ? style.innerPadding : ""
        }`}
      >
        <ServicesCart
          cart={cart}
          minDeliveryPrice={minPrice}
          maxDeliveryPrice={maxPrice}
          isTakeawayActive={state.isTakeawayMode}
          onIncreaseQuantityClicked={handleIncreaseQuantityClicked}
          onDecreaseQuantityClicked={handleDecreaseQuantityClicked}
        />
        {multipleDeliveryPrices && !state.isTakeawayMode && (
          <div className={style.cartMessageContainer}>
            <i className={`fal fa-asterisk mt-1 ${style.icon}`}></i>
            {I18n.t("service_ordering.different_delivery_price_notice")}
          </div>
        )}
        {!isGoodsTotalPriceEnough() && (
          <div className={style.cartMessageContainer}>
            <i className={`fal fa-asterisk mt-1 ${style.icon}`}></i>
            {I18n.t("service_ordering.cart_amount_notice", {
              minGoodsPrice: minGoodsPrice / 100,
            })}
          </div>
        )}
        {cartPromotion && cartPromotion.type === "discounted_cart" && (
          <DiscountedCartProgressBar
            cartSubtotal={
              !!cart.discount_amount ? cart.original_subtotal : cart.subtotal
            }
            minimumAmountCents={cartPromotion.minimum_amount_cents}
            discountPercentage={cartPromotion.percentage}
          />
        )}
        {!isModal && (
          <div className={style.cartFirstDeliveryContainer}>
            <AvailableDeliverySlots
              isTakeaway={state.isTakeawayMode}
              slots={state.deliverySlots}
            />
          </div>
        )}
        {!state.isTakeawayMode && (
          <div className={style.cartMessageContainer}>
            <i className={`fal fa-info-circle ${style.icon}`}></i>
            {I18n.t("service_ordering.max_cash_message", {
              value: ORDER_MAX_CASH_THRESHOLD,
            })}
          </div>
        )}
      </div>
      {isCheckoutButtonEnabled() && (
        <CheckoutButton
          onClick={handleCheckoutButtonClicked}
          disabled={state.checkoutInProgress}
        />
      )}
    </div>
  )
}
